// XY-Plot (Phasendiagramm): zwei Signale gegeneinander (x/y), Spur über die Zeit.
//
// Beide Reihen sind zeit-synchron (gleiche Ticks); je Tick-Index bildet (x[i], y[i]) einen Punkt.
// Reine SVG-Zeichnung mit automatischer Achsenskalierung — deterministisch, ohne Chart-Bibliothek.

import { type JSX } from 'react';

export interface XYSeries {
  xTag: string;
  yTag: string;
  color: string;
  x: { t: number; value: number }[];
  y: { t: number; value: number }[];
}

interface XYChartProps {
  series: XYSeries;
  width?: number;
  height?: number;
  emptyLabel: string;
}

const PADDING = { top: 12, right: 14, bottom: 26, left: 46 };

function span(values: number[]): { lo: number; hi: number } {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of values) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  if (!Number.isFinite(lo)) return { lo: 0, hi: 1 };
  if (hi === lo) {
    const pad = Math.abs(lo) > 0 ? Math.abs(lo) * 0.1 : 1;
    return { lo: lo - pad, hi: hi + pad };
  }
  return { lo, hi };
}

export function XYChart({
  series,
  width = 420,
  height = 320,
  emptyLabel,
}: XYChartProps): JSX.Element {
  const n = Math.min(series.x.length, series.y.length);
  const pts = Array.from({ length: n }, (_, i) => ({ x: series.x[i].value, y: series.y[i].value }));
  const hasData = pts.length > 0;

  const xs = span(pts.map((p) => p.x));
  const ys = span(pts.map((p) => p.y));
  const innerW = width - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;
  const sx = (x: number): number => PADDING.left + ((x - xs.lo) / (xs.hi - xs.lo)) * innerW;
  const sy = (y: number): number => PADDING.top + innerH - ((y - ys.lo) / (ys.hi - ys.lo)) * innerH;

  const grid = Array.from({ length: 5 }, (_, i) => i / 4);

  return (
    <svg
      role="img"
      aria-label="XY-Plot"
      data-testid="xy-chart"
      viewBox={`0 0 ${width} ${height}`}
      className="h-full w-full"
      preserveAspectRatio="none"
    >
      <rect
        x={PADDING.left}
        y={PADDING.top}
        width={innerW}
        height={innerH}
        fill="#0E2246"
        stroke="#1A3460"
      />
      {/* Gitter + Achsenbeschriftung */}
      {grid.map((g, i) => {
        const xv = xs.lo + (xs.hi - xs.lo) * g;
        const yv = ys.lo + (ys.hi - ys.lo) * g;
        return (
          <g key={i}>
            <line x1={sx(xv)} x2={sx(xv)} y1={PADDING.top} y2={PADDING.top + innerH} stroke="#1A3460" />
            <line x1={PADDING.left} x2={PADDING.left + innerW} y1={sy(yv)} y2={sy(yv)} stroke="#1A3460" />
            <text x={sx(xv)} y={height - 8} textAnchor="middle" fontSize={9} fill="#7A8FAF">
              {fmtTick(xv)}
            </text>
            <text x={PADDING.left - 5} y={sy(yv) + 3} textAnchor="end" fontSize={9} fill="#7A8FAF">
              {fmtTick(yv)}
            </text>
          </g>
        );
      })}
      {/* Spur */}
      {hasData && (
        <polyline
          data-testid="xy-trace"
          data-count={pts.length}
          fill="none"
          stroke={series.color}
          strokeWidth={1.6}
          points={pts.map((p) => `${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ')}
        />
      )}
      {/* aktueller Betriebspunkt hervorheben */}
      {hasData && (
        <circle cx={sx(pts[n - 1].x)} cy={sy(pts[n - 1].y)} r={3} fill={series.color} />
      )}
      {/* Achsentitel */}
      <text x={PADDING.left + innerW / 2} y={height - 20} textAnchor="middle" fontSize={10} fill="#EAEDF4">
        {series.xTag}
      </text>
      <text
        x={12}
        y={PADDING.top + innerH / 2}
        textAnchor="middle"
        fontSize={10}
        fill="#EAEDF4"
        transform={`rotate(-90 12 ${PADDING.top + innerH / 2})`}
      >
        {series.yTag}
      </text>
      {!hasData && (
        <text x={width / 2} y={height / 2} textAnchor="middle" fontSize={12} fill="#7A8FAF">
          {emptyLabel}
        </text>
      )}
    </svg>
  );
}

function fmtTick(v: number): string {
  if (Math.abs(v) >= 100) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(1);
  return v.toFixed(2);
}
